import React, { useContext, useEffect, useState } from "react";
import Field from "../core/ui/forms/field";
import SessionContext from "SessionContext";
import { useHistory } from "react-router-dom";
import { Alert, Form, FormGroup } from "reactstrap";
import DualSelect from "partials/dual-select";
import ConfirmDelete from "core/ui/confirm-delete";
import ConfirmSuccess from "../core/ui/confirm-success";
import UnsavedChanges from "partials/unsaved-changes";

export default function UserRoleForm(props) {
  const session = useContext(SessionContext);
  const history = useHistory();
  const [modified, setModified] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [role, setRole] = useState({name: "", description: "", permissions: []});
  const [disableSave, setDisableSave] = useState(true);
  const [roleEditing, setRoleEditing] = useState({});
  const [permissionsList, setPermissionsList] = useState();
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [confirmDeleteText, setConfirmDeleteText] = useState("Delete User Role?");
  const [allowReset, setAllowReset] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("Updated.");

  const onDeleteIconConfirmation = (i) => {
    setConfirmDeleteText(<div>
      Delete User Role {role.name}?
      <Alert color="warning" className="d-flex">
        <i className="bi bi-lightbulb"></i>
        <div>Note: If role is in use, it won't be deleted.</div>
      </Alert>
    </div>);
    setShowModalDelete(true);
  };

  const onDeleteIconConfirmed = () => {
    onDeleteRole();
    setShowModalDelete(false);
  };

  const transformPermissionsToDualSelect = (allPermissions) => {
     let transform = [];
     allPermissions.forEach((permission) => {
      transform.push({id: permission.id, label: permission.name});
     });
     return transform;
  }

  const transformPermissionsForApi = () => {
    let transform = [];
    for (let i = 0; i < selectedPermissions.length; i++) {
      permissionsList.forEach((permission) => {
        if(permission.id === selectedPermissions[i].id) {
          let permBuilder = {
            id: permission.id,
            name: permission.name,
            description: permission.description
          };
          transform.push(permBuilder);
        }
      });
    }
    return transform;
  }

  // for editing existing role
  function loadRole(role_id) {
    setLoaded(false);
    session
      .query("admin/get_role", { id: role_id })
      .then((response) => {
        setRole({
          id: role_id,
          name: response.name, 
          description: response.description, 
          permissions: transformPermissionsToDualSelect(response.permissions)
        });
        setSelectedPermissions(transformPermissionsToDualSelect(response.permissions));
        setRoleEditing(
          {
            id: role_id,
            name: response.name, 
            description: response.description, 
            permissions: transformPermissionsToDualSelect(response.permissions)
          }
        );
        setLoaded(true);
      })
  }

  function getAllPermissions() {
    setLoaded(false);
    session
      .query("admin/get_permissions", { id: 1 })
      .then((response) => {
        setPermissionsList(response); // store the original list
        setAllPermissions(transformPermissionsToDualSelect(response)); // to use with dual select
        setLoaded(true);
      })
  }

  useEffect(() => {
    getAllPermissions();
    if (props.args.role_id) {
      loadRole(props.args.role_id);
    } 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let sortList = (list) => {
      if(list !== undefined) {
        let runSort = list.sort(function(a, b) {
          const nameA = a.label.toUpperCase();
          const nameB = b.label.toUpperCase();
          if (nameA < nameB) {
            return -1;
          }
          return 1;
        });
        return runSort;
      }
    };

    let sortedAvailablePermissions = sortList(allPermissions);
    let sortedSelectedPermissions = sortList(selectedPermissions);
    let sortedEditingPermissions = sortList(roleEditing.permissions);

    setAllPermissions(sortedAvailablePermissions);
    setSelectedPermissions(sortedSelectedPermissions);
    setRoleEditing({...roleEditing, permissions: sortedEditingPermissions});
   // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPermissions, selectedPermissions])

  useEffect(() => {
    const storeOldValues = () => {
      if (role.id !== roleEditing.id) {
        setRoleEditing(role);
      }
    };

    const checkForEmpty = () => {
        if(role.name.length === 0) {
          setDisableSave(true);
        }
        else if(selectedPermissions[0] === undefined) {
          setDisableSave(true);
        } else {
          setDisableSave(false);
        }
    };

    const checkForChanges = () => {
      if(props.args.role_id) {
        const hasChanged =
        JSON.stringify(role) !== JSON.stringify(roleEditing);
        if (hasChanged) {
            setDisableSave(false);
            setModified(true);
        } else {
            setDisableSave(true);
            setModified(false);
        }
      }
    };

    if (role && Object.keys(role).length !== 0) {
      storeOldValues();
      checkForEmpty();
      checkForChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, roleEditing, selectedPermissions]);

  function resetAllFields() {
    setAllowReset(true);
    setModified(false);
    setRole({name: "", description: "", permissions: []});
  }

  function onClickSave(ev) {
    ev.stopPropagation();
    ev.preventDefault();

    // format data for API post
    const newRole = {
      ...role, permissions: transformPermissionsForApi()
    };

    if (props.args.role_id) {
      session
        .command(
          "admin/update_role",
          newRole,
          {},
          { defaultErrorHandling: false }
        )
        .then(() => {
          resetAllFields();
          setSuccessMessage("Updated Successfully.");
          setShowSuccess(true);
          setTimeout(() => {
            setShowSuccess(false);
            goBack();
          }, 2000);
        })
        .catch((error) => {
          console.log("Failed to update Role", JSON.stringify(error));
        });
    } else {
      session
        .command(
          "admin/create_role",
          newRole,
          {},
          { defaultErrorHandling: false }
        )
        .then(() => {
          resetAllFields();
          setSuccessMessage("Created Successfully.");
          setShowSuccess(true);
          setTimeout(() => {
            setShowSuccess(false);
            goBack();
          }, 2000);
        })
        .catch((error) => {
          console.log("Failed to create new Role", JSON.stringify(error));
        });
    }
  }

  function onDeleteRole() {
    session
      .command("admin/delete_role", role,
        {},
        { defaultErrorHandling: false }
      )
      .then(() => {
        resetAllFields();
        setSuccessMessage("Deleted Successfully.");
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          goBack();
        }, 2000);
      })
      .catch((error) => {
        console.log("Failed to delete Role", JSON.stringify(error));
      });
  }

  function setDescription(ev) {
    setRole({ ...role, description: ev.target.value });
    setModified(true);
  }

  function setName(ev) {
    setRole({ ...role, name: ev.target.value });
    setModified(true);
  }

  useEffect(() => {
    setRoleEditing({...roleEditing, permissions: selectedPermissions});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPermissions])

  function goBack() {
    history.push("/admin-user-roles");
  }

  function onClickAddAll(e) {
    e.preventDefault();
    setSelectedPermissions(allPermissions);
  }

  function onClickRemoveAll(e) {
    e.preventDefault();
    setSelectedPermissions([]);
  }

  useEffect(() => {
    const checkFieldsOnCreateNew = () => {
      if( (role.name || role.description || selectedPermissions.length) && !props.args.role_id ) {
        setModified(true);
      } else if(!role.id) {
        setModified(false);
      }
    }

    if(!allowReset) {
      checkFieldsOnCreateNew();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role.name, role.description, role.permissions, selectedPermissions, allowReset])

  return (
    loaded && (
      <div className="page admin-page">
        <div className="list-header sticky-page-header">
          <button className="new-button" onClick={goBack}>
            <i className="bi bi-chevron-left" /> Back
          </button>
          <h1>User Role</h1>
          <i className="spacer" />
          <div className="admin-form-buttons">
            <button
              className="new-button"
              onClick={onDeleteIconConfirmation}
              disabled={!props.args.role_id}
            >
              Delete This Role
            </button>
          </div>
          <div className="admin-form-buttons">
            <button
              className="new-button promote"
              onClick={onClickSave}
              disabled={disableSave}
            >
              Save Changes
            </button>
          </div>
        </div>
        <section className="admin-form-details mt-5">
          <Field label="* Role Name" className="admin-form-details-heading">
            <input
              type="text"
              value={role.name}
              className="admin-form-details-description"
              onChange={setName}
              placeholder="Required"
            />
          </Field>
          <Field label="Description" className="admin-form-details-heading">
            <input
              type="text"
              value={role.description}
              className="admin-form-details-description"
              onChange={setDescription}
              placeholder="Optional"
            />
          </Field>
          <br />
          <Form className="bs-form model-form exclusions margin-top-1" onSubmit={e => e.preventDefault()}>
            <FormGroup className="form-grp">
            <div className="d-inline-flex">
              <h5 className="pt-2">User Permissions:</h5>
              <div className="p-2"></div>
              <button className="new-button btn-add" onClick={(e) => onClickAddAll(e)}>
                <i className="bi bi-plus" /> Add All
              </button>
              <div className="p-2"></div>
              <button className="new-button btn-remove" onClick={(e) => onClickRemoveAll(e)}>
                <i className="bi bi-dash" />  Remove All
              </button>
            </div>
              <DualSelect
                optionLabel="label"
                allOptions={allPermissions}
                selectedOptions={selectedPermissions}
                setSelectedOptions={setSelectedPermissions}
              />
            </FormGroup>
          </Form>
        </section>
        <div>
          <ConfirmDelete
            show={showModalDelete}
            label={confirmDeleteText}
            proceed={onDeleteIconConfirmed}
            cancel={() => {setShowModalDelete(false)}}
          />
          <ConfirmSuccess show={showSuccess} label={successMessage} />
          <UnsavedChanges hasUnsavedChanges={modified} />
        </div>
      </div>
    )
  );
}
